import React, { useState } from 'react';
import { Pill, Check, Info, Trash2 } from 'lucide-react';
import { Box, Typography, Pressable, Input } from '@/shared/design-system/primitives';
import { ProtocolItem } from '@stores/v4/useSupplementStore';
import { EVENT_TYPES, EVENT_THEMES } from '@/shared/constants/event-types.v4';
import { DiagnosticCard } from '../../shared/DiagnosticCard';

interface SupplementChecklistProps {
    windows: string[];
    selectedWindow: string | null;
    setSelectedWindow: (w: string) => void;
    activeProtocol: any;
    checkedItems: Record<string, boolean>;
    toggleItem: (item: ProtocolItem) => void;
    onToggleAll: (checked: boolean) => void;
    onUpdateDosage: (itemId: string, amount: number) => void;
    customDosages: Record<string, number>;
}

/**
 * SupplementChecklist: Protocol editor with visual scaling and adjustable dosages.
 * V9.7: Integrated Dose Controller with Gram/Scoop logic for powders.
 */
export const SupplementChecklist: React.FC<SupplementChecklistProps> = ({
    windows,
    selectedWindow,
    setSelectedWindow,
    activeProtocol,
    checkedItems,
    toggleItem,
    onToggleAll,
    onUpdateDosage,
    customDosages
}) => {
    const themeColor = EVENT_THEMES[EVENT_TYPES.SUPPLEMENTATION];
    const formatLabel = (label: string) => label.replace(/_/g, ' ');

    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

    const visibleItems = activeProtocol?.items.filter((it: any) => 
        selectedWindow === 'ALL' || it.timing_window === selectedWindow
    ) || [];

    const isAllChecked = visibleItems.length > 0 && visibleItems.every((it: any) => !!checkedItems[it.id]);

    const handleToggleInfo = (id: string) => {
        setExpandedItemId(expandedItemId === id ? null : id);
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '25px' }}>
                <Pill size={28} color={themeColor} />
                <h3 style={{ fontSize: '18px', fontWeight: '950', color: '#fff', margin: 0, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    {selectedWindow ? `${formatLabel(selectedWindow)} SESSION` : "BIO-ACTIVE APOTHECARY"}
                </h3>
            </div>

            {/* 1. WINDOW SELECTOR */}
            <Box style={{ flexDirection: 'row', gap: 8, marginBottom: 25, flexWrap: 'wrap' }}>
                {windows.map(w => (
                    <Pressable 
                        key={w} 
                        onPress={() => setSelectedWindow(w)}
                        style={{ 
                            paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10,
                            backgroundColor: selectedWindow === w ? themeColor : 'rgba(255,255,255,0.02)',
                            borderWidth: 1, borderColor: selectedWindow === w ? themeColor : '#222'
                        }}
                    >
                        <Typography style={{ fontSize: 11, fontWeight: '950', color: selectedWindow === w ? '#000' : '#666', letterSpacing: 0.5 }}>
                            {formatLabel(w)}
                        </Typography>
                    </Pressable>
                ))}
            </Box>

            {/* 2. CHECKLIST (Protocol Items) */}
            {activeProtocol && selectedWindow && selectedWindow !== 'AD-HOC' && (
                <Box style={{ marginBottom: 25 }}>
                    {/* BULK TOGGLE ROW */}
                    <Pressable 
                        onPress={() => onToggleAll(!isAllChecked)}
                        style={{ 
                            padding: 15, borderRadius: 12, marginBottom: 12,
                            backgroundColor: 'rgba(255,255,255,0.03)',
                            borderWidth: 1, borderColor: '#222',
                            flexDirection: 'row', alignItems: 'center', gap: 14
                        }}
                    >
                        <Box style={{ 
                            width: 20, height: 20, borderRadius: 6, borderWidth: 1, 
                            borderColor: isAllChecked ? themeColor : '#444',
                            backgroundColor: isAllChecked ? themeColor : 'transparent',
                            alignItems: 'center', justifyContent: 'center'
                        }}>
                            {isAllChecked && <Check size={14} color="#fff" strokeWidth={3.5} />}
                        </Box>
                        <Typography style={{ fontSize: 11, fontWeight: '950', color: '#666', letterSpacing: 1.5 }}>
                            {isAllChecked ? 'DESELECT ALL' : 'SELECT ALL / NONE'}
                        </Typography>
                    </Pressable>

                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {visibleItems.map((item: any) => {
                            const isChecked = !!checkedItems[item.id];
                            const isExpanded = expandedItemId === item.id;
                            const cat = item.catalog_item || {};
                            const scoopWeight = Number(cat.manufacturer_scoop_weight || 0);
                            const isMassBased = cat.measure_unit === 'g' || cat.measure_unit === 'ml';
                            
                            const currentAmount = customDosages[item.id] !== undefined ? customDosages[item.id] : item.dosage_count;
                            const currentScoops = (isMassBased && scoopWeight > 0) ? (currentAmount / scoopWeight) : 0;
                            const scalar = (isMassBased && scoopWeight > 0) ? currentScoops : currentAmount;

                            const microSource = cat.baseline_micros || item.micros || {};
                            const scaledMicros = Object.fromEntries(
                                Object.entries(microSource).map(([k, v]) => {
                                    if (typeof v === 'number') return [k, { amount: v * scalar, unit: 'mg' }];
                                    if (typeof v === 'object' && v !== null && 'amount' in (v as any)) {
                                        return [k, { ...(v as any), amount: (v as any).amount * scalar }];
                                    }
                                    if (typeof v === 'string') {
                                        const numericPart = parseFloat(v.replace(/[^0-9.]/g, '')) || 0;
                                        const unitPart = v.replace(/[0-9.]/g, '').trim();
                                        return [k, { amount: numericPart * scalar, unit: unitPart }];
                                    }
                                    return [k, v];
                                })
                            );

                            return (
                                <Box key={item.id}>
                                    <Box 
                                        style={{ 
                                            padding: 18, borderRadius: 14, 
                                            backgroundColor: isChecked ? `${themeColor}12` : 'rgba(255,255,255,0.02)',
                                            borderWidth: 1, borderColor: isChecked ? (isExpanded ? themeColor : `${themeColor}40`) : '#111',
                                            flexDirection: 'row', alignItems: 'center', gap: 14,
                                            boxShadow: isChecked ? `0 4px 20px rgba(0,0,0,0.4)` : 'none',
                                            transition: 'all 0.2s ease'
                                        } as any}
                                    >
                                        <Pressable onPress={() => toggleItem(item)}>
                                            <Box style={{ 
                                                width: 20, height: 20, borderRadius: 6, borderWidth: 1, 
                                                borderColor: isChecked ? themeColor : '#333',
                                                backgroundColor: isChecked ? themeColor : 'transparent',
                                                alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                {isChecked && <Check size={14} color="#fff" strokeWidth={3.5} />}
                                            </Box>
                                        </Pressable>

                                        <Box style={{ flex: 1 }}>
                                            <Typography style={{ fontSize: 15, fontWeight: '900', color: isChecked ? '#fff' : '#aaa' }}>
                                                {cat.product_name}
                                            </Typography>
                                            
                                            {/* DOSAGE CONTROLLER */}
                                            <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                                <Box style={{ backgroundColor: '#0a0a0a', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4, flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#222' }}>
                                                    <Input 
                                                        keyboardType="numeric"
                                                        value={String(currentAmount)}
                                                        onChangeText={(val) => onUpdateDosage(item.id, parseFloat(val) || 0)}
                                                        style={{ width: 35, textAlign: 'right', fontSize: 12, fontWeight: '900', color: isChecked ? themeColor : '#666', padding: 0, backgroundColor: 'transparent', borderBottomWidth: 0 } as any}
                                                    />
                                                    <Typography style={{ fontSize: 9, color: '#444', fontWeight: 'bold', marginLeft: 2 }}>{cat.measure_unit}</Typography>
                                                </Box>

                                                {isMassBased && scoopWeight > 0 && (
                                                    <Box style={{ backgroundColor: '#0a0a0a', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4, flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#222', opacity: 0.6 }}>
                                                        <Input 
                                                            keyboardType="numeric"
                                                            value={String(currentScoops)}
                                                            onChangeText={(val) => onUpdateDosage(item.id, (parseFloat(val) || 0) * scoopWeight)}
                                                            style={{ width: 30, textAlign: 'right', fontSize: 11, fontWeight: '800', color: '#555', padding: 0, backgroundColor: 'transparent', borderBottomWidth: 0 } as any}
                                                        />
                                                        <Typography style={{ fontSize: 8, color: '#333', fontWeight: 'bold', marginLeft: 2 }}>{cat.volumetric_label || 'SCOOPS'}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        
                                        <Pressable 
                                            onPress={(e: any) => { e.stopPropagation(); handleToggleInfo(item.id); }} 
                                            style={{ 
                                                width: 36, height: 34, borderRadius: 12, 
                                                backgroundColor: isExpanded ? `${themeColor}22` : 'rgba(255,255,255,0.03)', 
                                                borderWidth: 1, borderColor: isExpanded ? themeColor : (isChecked ? `${themeColor}33` : 'rgba(255,255,255,0.08)'),
                                                alignItems: 'center', justifyContent: 'center'
                                            }}
                                        >
                                            <Info size={16} color={isExpanded ? '#fff' : (isChecked ? themeColor : "#666")} />
                                        </Pressable>
                                    </Box>

                                    <DiagnosticCard data={scaledMicros} isVisible={isExpanded} themeColor={themeColor} />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </>
    );
};
